import { visitStatusEnum } from '@markku/db'
import { z } from 'zod'

// Event type enum
export const eventTypeEnum = z.enum([
  'visit',
  'training',
  'meeting',
  'break',
  'survey',
  'other',
  'availability',
])

export type EventType = z.infer<typeof eventTypeEnum>

// Visit status type (reuse from db enum)
export type VisitStatus = (typeof visitStatusEnum.enumValues)[number]

// Input schemas for stats endpoints
export const visitStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  eventTypes: z.array(eventTypeEnum).optional(),
  statuses: z.array(z.enum(visitStatusEnum.enumValues)).optional(),
  employeeIds: z.array(z.number()).optional(),
  maintenanceTaskIds: z.array(z.number()).optional(),
})

// Weekly visit data
export const weeklyVisitDataSchema = z.object({
  weekStart: z.string(), // ISO date string (Monday of the week)
  weekEnd: z.string(), // ISO date string (Sunday of the week)
  total: z.number(),
  byStatus: z.object({
    scheduled: z.number(),
    in_progress: z.number(),
    completed: z.number(),
    cancelled: z.number(),
  }),
  byEmployee: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      count: z.number(),
    }),
  ),
})

// Weekly revenue data
export const weeklyRevenueDataSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  totalRevenue: z.number(),
  byEmployee: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      revenue: z.number(),
    }),
  ),
  byBillingType: z.object({
    subscription_included: z.number(),
    separately_invoiced: z.number(),
  }),
})

// Summary statistics
export const visitStatsSummarySchema = z.object({
  totalVisits: z.number(),
  completedVisits: z.number(),
  cancelledVisits: z.number(),
  scheduledVisits: z.number(),
  completionRate: z.number(), // 0-1
  cancellationRate: z.number(), // 0-1
  totalRevenue: z.number(),
  averageRevenuePerVisit: z.number(),
  byEventType: z.array(
    z.object({
      eventType: z.string(),
      count: z.number(),
    }),
  ),
  topEmployees: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      visitCount: z.number(),
      completedCount: z.number(),
      scheduledCount: z.number(),
      revenue: z.number(),
    }),
  ),
})

// Response schemas
export const visitStatsWeeklyResponseSchema = z.object({
  weeks: z.array(weeklyVisitDataSchema),
})

export const visitStatsRevenueResponseSchema = z.object({
  weeks: z.array(weeklyRevenueDataSchema),
})

// Commission schemas
export const commissionRowSchema = z.object({
  userId: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  standingCommissionPercent: z.number().nullable(),
  totalRevenue: z.number(),
  autoCommission: z.number(),
  manualCommission: z.number(),
  commission: z.number(),
})

/** @deprecated Use commissionRowSchema */
export const subcontractorCommissionRowSchema = commissionRowSchema

export const commissionsResponseSchema = z.object({
  rows: z.array(commissionRowSchema),
  totals: z.object({
    totalRevenue: z.number(),
    totalAutoCommission: z.number(),
    totalManualCommission: z.number(),
    totalCommission: z.number(),
  }),
})

/** @deprecated Use commissionsResponseSchema */
export const subcontractorCommissionsResponseSchema = commissionsResponseSchema

// Types
export type VisitStatsInput = z.infer<typeof visitStatsInputSchema>
export type WeeklyVisitData = z.infer<typeof weeklyVisitDataSchema>
export type WeeklyRevenueData = z.infer<typeof weeklyRevenueDataSchema>
export type VisitStatsSummary = z.infer<typeof visitStatsSummarySchema>
export type VisitStatsWeeklyResponse = z.infer<
  typeof visitStatsWeeklyResponseSchema
>
export type VisitStatsRevenueResponse = z.infer<
  typeof visitStatsRevenueResponseSchema
>
export type CommissionRow = z.infer<typeof commissionRowSchema>
export type CommissionsResponse = z.infer<typeof commissionsResponseSchema>

/** @deprecated Use CommissionRow */
export type SubcontractorCommissionRow = CommissionRow
/** @deprecated Use CommissionsResponse */
export type SubcontractorCommissionsResponse = CommissionsResponse

// ============================================================================
// Subscription Statistics
// ============================================================================

// Input schema for subscription stats
export const subscriptionStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
})

// Monthly subscription data
export const monthlySubscriptionDataSchema = z.object({
  monthStart: z.string(), // ISO date string (first day of month)
  monthEnd: z.string(), // ISO date string (last day of month)
  mrr: z.number(), // Monthly Recurring Revenue at end of month
  activeCount: z.number(), // Active subscriptions at end of month
  newCount: z.number(), // New subscriptions started in month
  churnedCount: z.number(), // Subscriptions ended/cancelled in month
})

// Summary statistics for subscriptions
export const subscriptionStatsSummarySchema = z.object({
  currentMrr: z.number(),
  activeSubscriptions: z.number(),
  averageSubscriptionValue: z.number(),
  newSubscriptionsInPeriod: z.number(),
  churnedSubscriptionsInPeriod: z.number(),
})

// Response schemas
export const subscriptionMonthlyResponseSchema = z.object({
  months: z.array(monthlySubscriptionDataSchema),
})

// Types
export type SubscriptionStatsInput = z.infer<
  typeof subscriptionStatsInputSchema
>
export type MonthlySubscriptionData = z.infer<
  typeof monthlySubscriptionDataSchema
>
export type SubscriptionStatsSummary = z.infer<
  typeof subscriptionStatsSummarySchema
>
export type SubscriptionMonthlyResponse = z.infer<
  typeof subscriptionMonthlyResponseSchema
>

// ============================================================================
// Employee Utilization Statistics
// ============================================================================

// Employee type filter
export const employeeTypeFilterEnum = z.enum([
  'all',
  'employees',
  'subcontractors',
])
export type EmployeeTypeFilter = z.infer<typeof employeeTypeFilterEnum>

// Input schema for employee utilization stats (extends visit stats input)
export const employeeStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  employeeType: employeeTypeFilterEnum.optional(),
  employeeIds: z.array(z.number()).optional(),
})

// Weekly utilization data per employee
export const weeklyEmployeeUtilizationSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  workdayCount: z.number(), // Number of workdays in the week (Mon-Fri)
  capacityHours: z.number(), // workdayCount * 8
  byEmployee: z.array(
    z.object({
      userId: z.number(),
      name: z.string(),
      isSubcontractor: z.boolean(),
      totalHours: z.number(), // Total hours scheduled/worked
      billableHours: z.number(), // Hours from visit event type
      nonBillableHours: z.number(), // Hours from training, meeting, etc.
      utilizationRate: z.number(), // totalHours / capacityHours (0-1+, can exceed 1)
      billableRate: z.number(), // billableHours / totalHours (0-1)
    }),
  ),
})

// Summary statistics for employee utilization
export const employeeStatsSummarySchema = z.object({
  totalEmployees: z.number(),
  employeesCount: z.number(), // Regular employees
  subcontractorsCount: z.number(),
  averageUtilizationRate: z.number(), // 0-1
  totalHoursWorked: z.number(),
  totalBillableHours: z.number(),
  averageRevenuePerHour: z.number(),
  // Feedback stats
  averageRating: z.number().nullable(), // Average rating across all employees (null if no feedbacks)
  feedbackRate: z.number(), // 0-1, percentage of visits with feedback
  totalFeedbackCount: z.number(),
})

// Employee utilization row for table
export const employeeUtilizationRowSchema = z.object({
  userId: z.number(),
  employeeId: z.number(),
  name: z.string(),
  role: z.string(),
  isSubcontractor: z.boolean(),
  totalHours: z.number(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  utilizationRate: z.number(),
  billableRate: z.number(),
  revenue: z.number(),
  revenuePerHour: z.number(),
  // Feedback stats
  averageRating: z.number().nullable(), // null if no feedbacks
  feedbackCount: z.number(),
  feedbackRate: z.number(), // 0-1, percentage of visits with feedback
  positiveRate: z.number(), // 0-1, percentage of ratings that are 4-5 stars
})

// Response schemas
export const employeeUtilizationWeeklyResponseSchema = z.object({
  weeks: z.array(weeklyEmployeeUtilizationSchema),
})

export const employeeUtilizationTableResponseSchema = z.object({
  rows: z.array(employeeUtilizationRowSchema),
  totals: z.object({
    totalHours: z.number(),
    billableHours: z.number(),
    nonBillableHours: z.number(),
    averageUtilizationRate: z.number(),
    averageBillableRate: z.number(),
    totalRevenue: z.number(),
    averageRevenuePerHour: z.number(),
    // Feedback totals
    averageRating: z.number().nullable(),
    totalFeedbackCount: z.number(),
    feedbackRate: z.number(),
  }),
})

// Types
export type EmployeeStatsInput = z.infer<typeof employeeStatsInputSchema>
export type WeeklyEmployeeUtilization = z.infer<
  typeof weeklyEmployeeUtilizationSchema
>
export type EmployeeStatsSummary = z.infer<typeof employeeStatsSummarySchema>
export type EmployeeUtilizationRow = z.infer<
  typeof employeeUtilizationRowSchema
>
export type EmployeeUtilizationWeeklyResponse = z.infer<
  typeof employeeUtilizationWeeklyResponseSchema
>
export type EmployeeUtilizationTableResponse = z.infer<
  typeof employeeUtilizationTableResponseSchema
>

// ============================================================================
// Employee Commission Statistics
// ============================================================================

// My Commissions
export const myCommissionsInputSchema = z.object({
  monthCount: z.number().min(1).max(24).default(6),
})

export const myCommissionsMonthSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  totalRevenue: z.number(),
  commission: z.number(),
  autoCommission: z.number(),
  manualCommission: z.number(),
  confirmedCommission: z.number(),
  estimatedCommission: z.number(),
})

export const myCommissionsResponseSchema = z.object({
  months: z.array(myCommissionsMonthSchema),
  currentMonthCommission: z.number(),
  currentMonthConfirmedCommission: z.number(),
  currentMonthEstimatedCommission: z.number(),
  isSubcontractor: z.boolean(),
})

// Commission Leaderboard
export const commissionLeaderboardInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
})

export const leaderboardEntrySchema = z.object({
  rank: z.number(),
  commission: z.number(),
  totalRevenue: z.number(),
  isCurrentUser: z.boolean(),
  name: z.string().nullable(),
})

export const commissionLeaderboardResponseSchema = z.object({
  entries: z.array(leaderboardEntrySchema),
  totalParticipants: z.number(),
})

// Maintenance Recommendation Stats
export const maintenanceRecommendationStatsResponseSchema = z.object({
  totalReported: z.number(),
  open: z.number(),
  won: z.number(),
  lost: z.number(),
  hitRate: z.number(), // 0-1, won / (won + lost)
})

// Types
export type MyCommissionsInput = z.infer<typeof myCommissionsInputSchema>
export type MyCommissionsMonth = z.infer<typeof myCommissionsMonthSchema>
export type MyCommissionsResponse = z.infer<typeof myCommissionsResponseSchema>
export type CommissionLeaderboardInput = z.infer<
  typeof commissionLeaderboardInputSchema
>
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>
export type CommissionLeaderboardResponse = z.infer<
  typeof commissionLeaderboardResponseSchema
>
export type MaintenanceRecommendationStatsResponse = z.infer<
  typeof maintenanceRecommendationStatsResponseSchema
>

// ============================================================================
// Resident App Statistics
// ============================================================================

export const residentAppStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
})

export const residentAppStatsResponseSchema = z.object({
  overview: z.object({
    totalUsers: z.number(), // all-time resident user count (context)
    newUsers: z.number(), // registered in period
    loggedInUsers: z.number(), // unique users who logged in in period
    totalLogins: z.number(), // total login events in period
    marketingConsentCount: z.number(), // consents given in period
  }),
  engagementTiers: z.object({
    oneTime: z.number(), // 1 login in period
    light: z.number(), // 2-5 logins in period
    regular: z.number(), // 6+ logins in period
  }),
  loginActivity: z.array(
    z.object({
      month: z.string(),
      logins: z.number(),
      uniqueUsers: z.number(),
    }),
  ),
  platforms: z.object({
    ios: z.number(),
    android: z.number(),
    desktop: z.number(),
  }),
  maintenanceTasks: z.object({
    totalCreated: z.number(),
    totalCompleted: z.number(),
  }),
  featureUsage: z.object({
    feedbackCount: z.number(),
    feedbackAvgRating: z.number().nullable(),
    chatMessageCount: z.number(),
    propertyEditCount: z.number(),
    fileUploadCount: z.number(),
    termsAcceptedCount: z.number(),
  }),
  marketingConsentMonthly: z.array(
    z.object({
      month: z.string(),
      count: z.number(),
    }),
  ),
})

export type ResidentAppStatsInput = z.infer<typeof residentAppStatsInputSchema>
export type ResidentAppStatsResponse = z.infer<
  typeof residentAppStatsResponseSchema
>
